import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ReceiveApiAdapter } from '@modules/Reused/Receive/adapters/ReceiveApiAdapter';
import { EntryApiAdapter } from '../adapters/EntryApiAdapter';
import { Package, Reception } from '../models/Reception';

async function fetchDestinationReference(
  pkg: Package,
  entryAdapter: EntryApiAdapter,
  receiveAdapter: ReceiveApiAdapter,
): Promise<string | null> {
  if (!pkg.destination_id || !pkg.destination_type) return null;
  try {
    switch (pkg.destination_type) {
      case 'INBOUND': {
        const entry = await entryAdapter.getEntry(pkg.destination_id);
        const ref = entry?.reference;
        return typeof ref === 'string' && ref.trim() ? ref.trim() : null;
      }
      case 'REJECTED':
      case 'RETURN': {
        const res = await receiveAdapter.getReception(pkg.destination_id);
        const code = res.data?.code;
        return typeof code === 'string' && code.trim() ? code.trim() : null;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/**
 * Resuelve la referencia visible del destino (entrada / reused) con llamadas a API,
 * ya que el detalle de recepción no incluye esas relaciones.
 */
export function useReceptionDestinationRefs(reception: Reception | null | undefined) {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const entryAdapter = useMemo(() => new EntryApiAdapter(httpClient), [httpClient]);
  const receiveAdapter = useMemo(() => new ReceiveApiAdapter(httpClient), [httpClient]);

  const packagesToResolve = useMemo(() => {
    if (!reception?.packages?.length) return [];
    return reception.packages.filter(
      (p) =>
        p &&
        p.status === 'IDENTIFIED' &&
        p.destination_type &&
        p.destination_id != null &&
        (p.destination_type === 'INBOUND' || p.destination_type === 'REJECTED' || p.destination_type === 'RETURN'),
    );
  }, [reception?.packages]);

  const queries = useQueries({
    queries: packagesToResolve.map((pkg) => ({
      queryKey: ['reception-destination-ref', reception?.id, pkg.id, pkg.destination_type, pkg.destination_id],
      queryFn: () => fetchDestinationReference(pkg, entryAdapter, receiveAdapter),
      enabled: Boolean(reception?.id && pkg.destination_id != null),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const refByPackageId = new Map<number, string>();
  packagesToResolve.forEach((pkg, index) => {
    const val = queries[index]?.data;
    if (typeof val === 'string' && val.trim()) {
      refByPackageId.set(pkg.id, val.trim());
    }
  });

  return { refByPackageId };
}
